import * as React from 'react';
import { CartesianGrid, Customized, Label, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ChartTooltip from './ChartTooltip';
import { Config, makeStyleResolver } from '../helpers';

const markerFillTypes = {
  filled: 'filled',
  empty: 'empty',
};

const lineStyles = {
  solid: null,
  dashed: "6 4",
  dotted: "2 2",
};

const markerRenderers = {
  circle: ({ cx, cy, stroke, index, isFilled }) => (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
      key={`circle-${index}`}
    />
  ),

  square: ({ cx, cy, stroke, index, isFilled }) => (
    <rect
      x={cx - 4}
      y={cy - 4}
      width={8}
      height={8}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
      key={`square-${index}`}
    />
  ),

  triangle: ({ cx, cy, stroke, index, isFilled }) => (
    <polygon
      key={`triangle-${index}`}
      points={`${cx},${cy - 5} ${cx + 5},${cy + 5} ${cx - 5},${cy + 5}`}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
    />
  ),

  diamond: ({ cx, cy, stroke, index, isFilled }) => (
    <polygon
      key={`diamond-${index}`}
      points={`${cx},${cy - 5} ${cx + 5},${cy} ${cx},${cy + 5} ${cx - 5},${cy}`}
      fill={isFilled ? stroke : 'white'}
      stroke={stroke}
      strokeWidth={2}
    />
  ),

  cross: ({ cx, cy, stroke, index }) => (
    <g key={`cross-${index}`} stroke={stroke} strokeWidth={2}>
      <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy}/>
      <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5}/>
    </g>
  ),
};

// `resolveStyle` is helpers.makeStyleResolver's product: one call returns all
// four attributes for a group id, from the colour rules first and the literal
// colorsMap table second.
function renderLegend(resolveStyle) {
  return ({ payload }) => (
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', padding: '10px 0' }}>
      {payload.filter(entry => !String(entry.dataKey).endsWith('__dom')).map((entry, index) => {
        const resolved = resolveStyle(entry.dataKey);
        // Band hi edge (`__band`) is always dashed, regardless of the group's own style.
        const isBandEdge = String(entry.dataKey).toLowerCase().endsWith('__band');
        const lineStyleDash = isBandEdge ? lineStyles.dashed : (lineStyles[resolved.lineStyle] ?? null);
        const markerShapeName = resolved.markerShape;
        const isFilled = resolved.markerFill === markerFillTypes.filled;

        return (
          <div
            key={`legend-item-${index}`}
            style={{ display: 'flex', alignItems: 'center', marginRight: '20px', marginBottom: '5px' }}
          >
            <svg width="40" height="20" style={{ marginRight: '5px' }}>
              <line x1="0" y1="10" x2="30" y2="10" stroke={entry.color} strokeWidth="2" strokeDasharray={lineStyleDash}/>
              {markerShapeName === 'circle' && (
                <circle cx="15" cy="10" r="4" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'square' && (
                <rect x="11" y="6" width="8" height="8" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'triangle' && (
                <polygon points="15,5 20,15 10,15" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'diamond' && (
                <polygon points="15,5 20,10 15,15 10,10" fill={isFilled ? entry.color : 'white'} stroke={entry.color} strokeWidth="2"/>
              )}
              {markerShapeName === 'cross' && (
                <g stroke={entry.color} strokeWidth="2">
                  <line x1="10" y1="10" x2="20" y2="10"/>
                  <line x1="15" y1="5" x2="15" y2="15"/>
                </g>
              )}
            </svg>
            <span style={{ fontSize: '14px' }}>{entry.value}</span>
          </div>
        );
      })}
    </div>
  );
}

// Shaded band fill (§1.5, band.mode:"area"), drawn in a <Customized> layer so it
// can use the chart's real axis scales (passed via Recharts props/state) to map
// each group's lo/hi percentile curves to pixels and fill between them. Rendered
// before the lines so the edge lines and dots stay on top.
function BandLayer({ xAxisMap, yAxisMap, groups }) {
  const sx = xAxisMap && Object.values(xAxisMap)[0]?.scale;
  const sy = yAxisMap && Object.values(yAxisMap)[0]?.scale;
  if (!sx || !sy || !groups?.length) return null;
  return (
    <g className='nv-chart__band'>
      {groups.map(g => {
        if (!g.lo.length || !g.hi.length) return null;
        const forward = g.lo.map(p => `${sx(p.x)},${sy(p.y)}`);
        const backward = g.hi.slice().reverse().map(p => `${sx(p.x)},${sy(p.y)}`);
        return (
          <polygon key={g.id} points={[...forward, ...backward].join(' ')} fill={g.color} fillOpacity={0.15} stroke='none'/>
        );
      })}
    </g>
  );
}

export default function LineChartWidget({ chartData, chartGroups, chartConfig, lineType = Config.Styles.Chart.DefaultLineStyle, bandArea = null }) {
  const [hoveredDot, setHoveredDot] = React.useState(null);

  // Memoized on the config so the rule regexes compile once per config rather
  // than once per line per render, and so the resolver's own per-id cache
  // survives re-renders.
  const resolveStyle = React.useMemo(() => makeStyleResolver(chartConfig), [chartConfig]);

  // Resolve each band group's colour once (through the same resolver as the
  // lines) so the fill matches its edge lines; the pixel mapping happens inside
  // BandLayer.
  const bandGroups = React.useMemo(
    () => (bandArea || []).map(g => ({ ...g, color: resolveStyle(g.id).color })),
    [bandArea, resolveStyle]
  );

  return (
    <div>
      <ResponsiveContainer width={Config.Styles.Chart.Width} height={Config.Styles.Chart.Height}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3"/>
          {bandGroups.length > 0 && <Customized component={<BandLayer groups={bandGroups}/>}/>}
          <XAxis
            type="number"
            tickFormatter={value => value}
            dataKey={chartConfig.axes.x.field}
            tick={Config.Styles.Chart.Axis.Tick}
            axisLine={Config.Styles.Chart.Axis.AxisLine}>
            <Label value={chartConfig.axes.x.label} position="bottom" offset={0}/>
          </XAxis>
          <YAxis
            type="number"
            tickFormatter={value => value}
            tick={Config.Styles.Chart.Axis.Tick}
            axisLine={Config.Styles.Chart.Axis.AxisLine}>
            <Label value={chartConfig.axes.y.label} angle={-90} position="inside" dx={-35}/>
          </YAxis>
          <Tooltip cursor={false} isAnimationActive={false} content={<ChartTooltip chartConfig={chartConfig} hoveredDot={hoveredDot}/>}/>
          <Legend content={renderLegend(resolveStyle)}/>

          {chartGroups.map((g) => {
            const resolved = resolveStyle(g.id);
            const color = resolved.color;
            // A band's hi edge is dashed to read as the upper bound of the range (§1.5).
            const lineStyle = g.band === 'hi'
              ? lineStyles.dashed
              : (lineStyles[resolved.lineStyle] ?? null);
            const markerShape = markerRenderers[resolved.markerShape] ?? markerRenderers.circle;
            const isFilled = resolved.markerFill === markerFillTypes.filled;

            return (
              <Line
                key={g.id}
                dataKey={g.id}
                name={g.label}
                connectNulls={true}
                type={lineType}
                strokeWidth={2}
                stroke={color}
                strokeDasharray={lineStyle}
                dot={({ value, payload: dotPayload, ...p }) => {
                  if (value === null) return null;
                  return (
                    <g
                      key={`dot-${g.id}-${p.index}`}
                      onMouseEnter={() => setHoveredDot({ dataKey: g.id, entry: dotPayload[`${g.id}__entry`], stroke: color, xField: g.xField })}
                      onMouseLeave={() => setHoveredDot(null)}
                    >
                      <circle cx={p.cx} cy={p.cy} r={10} fill="transparent"/>
                      {markerShape({...p, isFilled})}
                    </g>
                  );
                }}
                activeDot={null}
              />
            );
          })}

          {chartConfig.pareto?.frontierOnly && chartConfig.pareto?.showDominated && chartGroups.map((g) => {
            const resolved = resolveStyle(g.id);
            const color = resolved.color;
            const markerShape = markerRenderers[resolved.markerShape] ?? markerRenderers.circle;
            // Dominated (off-frontier) points: markers only — no connecting line,
            // no legend entry. Hollow fill sets them apart from frontier points.
            return (
              <Line
                key={`${g.id}__dom`}
                dataKey={`${g.id}__dom`}
                connectNulls={false}
                stroke="none"
                legendType="none"
                isAnimationActive={false}
                dot={({ value, payload: dotPayload, ...p }) => {
                  if (value === null || value === undefined) return null;
                  return (
                    <g
                      key={`dom-${g.id}-${p.index}`}
                      onMouseEnter={() => setHoveredDot({ dataKey: g.id, entry: dotPayload[`${g.id}__dom__entry`], stroke: color, xField: g.xField })}
                      onMouseLeave={() => setHoveredDot(null)}
                    >
                      <circle cx={p.cx} cy={p.cy} r={10} fill="transparent"/>
                      {markerShape({...p, isFilled: false})}
                    </g>
                  );
                }}
                activeDot={null}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
